import submitWebHook from '@/requests/magic-flow/submitWebHook';
import pollUntilCompleted from '@/requests/magic-flow/pollUntilCompleted';
import { MAGIC_FLOW_WEBHOOK_URL } from '@/consts/urls';
import buildParamsFromInputs from '@/utils/flow/buildParamsFromInputs';
import createNotificationConfig from '@/utils/ui/createNotificationConfig';
import { saveGeneration } from '@/utils/storage/generationStorage';
import buildFlowMedia from '@/utils/generation/buildFlowMedia';

import type { IInput, Preset } from '@/types';
import type { BuiltFlowMedia } from '@/utils/generation/buildFlowMedia';

export interface SubmitAndPollResult extends BuiltFlowMedia {
  generationId: string;
}

/**
 * Submits the webhook, persists the generation for crash recovery, then
 * polls until completion and parses the result into a `FlowMedia` shape.
 *
 * Uses the same `pollUntilCompleted` + `buildFlowMedia` pair that the main
 * `useGenerate` polling pipeline uses, so both flows share one source of
 * truth for result parsing. `buildFlowMedia` throws when the poll response
 * carries no result URL — caller treats that as a generation failure.
 */
const submitAndPollGeneration = async ({
  webhookId,
  selectedPreset,
  inputs,
  toolId,
  folderName,
  placeholderUuid,
  startedAt,
}: {
  webhookId: string;
  selectedPreset: Preset;
  inputs: IInput[];
  toolId: string;
  folderName: string;
  placeholderUuid: string;
  startedAt: number;
}): Promise<SubmitAndPollResult> => {
  // Build params and submit webhook
  const webhookParams = buildParamsFromInputs(inputs);
  const webhookResponse = await submitWebHook({
    webhookId,
    params: webhookParams,
    driveOptions: {
      folderName,
      packageId: toolId,
    },
    notificationConfig: createNotificationConfig(),
  });
  const generationId = webhookResponse.id;

  await saveGeneration({
    generationId,
    placeholderUuid,
    presetTitle: selectedPreset.title || '',
    startedAt,
    presetType: selectedPreset.type,
  });

  // Poll for result (same machinery the main generation pipeline uses)
  const pollUrl = `${MAGIC_FLOW_WEBHOOK_URL}/${generationId}/result`;
  const pollingResponse = await pollUntilCompleted({ url: pollUrl });
  const built = buildFlowMedia({ generationId, pollingResponse });

  return { generationId, ...built };
};

export default submitAndPollGeneration;
